.page {
  /* Fill the viewport with a constant gutter rather than centring inside a
     max-width — past that width the margins would grow instead of the table. */
  padding: var(--space-6) 0;
  width: 100%;
  background: var(--bg-base);
  min-height: 100vh;
  /* Backstop: nothing here should overflow, but if it ever does it stays
     inside the page rather than pushing the whole shell sideways. */
  overflow-x: clip;
}

/* ── Greeting ────────────────────────────────────────────── */

.head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--space-4);
  flex-wrap: wrap;
  margin-bottom: var(--space-6);
}

.greeting {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--text-2xl);
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-tight);
  color: var(--text-primary);
  line-height: 1.2;
}

.welcome {
  margin: var(--space-1) 0 0 0;
  font-size: var(--text-base);
  color: var(--text-secondary);
}

/* Region + time period — scope for everything below them. Hugs the right
   gutter so the two selects line up with the content's right edge. */
.scope {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-2);
  flex-wrap: wrap;
}

/* Fixed, comfortable width so labels ('All regions', 'Q2 2026') never wrap in
   the trigger or the menu. Both selects share it, so they're equal width. */
.scopeSelect {
  width: 180px;
}

/* ── Platform strip ──────────────────────────────────────── */

.stripSection {
  margin-bottom: var(--space-10);
}

/* Five chips that flex to fit, wrapping rather than scrolling. No horizontal
   scroller means there is no way for this row to escape the page. */
.strip {
  display: flex;
  align-items: stretch;
  gap: var(--space-3);
  flex-wrap: wrap;
  min-width: 0;
}

.seeAll {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  padding: 0 var(--space-4);
  border: 1px dashed var(--border-default);
  border-radius: var(--radius-lg);
  font-size: var(--text-sm);
  font-weight: var(--weight-semi);
  color: var(--text-secondary);
  text-decoration: none;
  white-space: nowrap;
  transition:
    color var(--duration-fast) var(--ease-default),
    border-color var(--duration-fast) var(--ease-default);
}

.seeAll:hover {
  color: var(--accent-secondary-text);
  border-color: var(--accent-secondary);
}

.chipSkeleton {
  flex: 1 1 160px;
  height: 62px;
  border-radius: var(--radius-lg);
  background: linear-gradient(
    90deg,
    var(--bg-card) 0%,
    var(--bg-elevated) 50%,
    var(--bg-card) 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s var(--ease-default) infinite;
}

@keyframes shimmer {
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
}

@media (prefers-reduced-motion: reduce) {
  .chipSkeleton {
    animation: none;
  }
}

/* ── Top 10s ─────────────────────────────────────────────── */

.topSection {
  padding-top: var(--space-8);
}

/* Title left, controls right — the d360 ContentCarousel section pattern */
.sectionHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  margin-bottom: var(--space-5);
  flex-wrap: wrap;
}

.sectionTitleRow {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin: 0;
}

.sectionTitle {
  font-family: var(--font-sans);
  font-size: var(--text-xl);
  font-weight: var(--weight-bold);
  color: var(--text-primary);
  line-height: 1.25;
}

/* Thin separator between the title and the inline leaderboard link. */
.sectionTitleDivider {
  color: var(--border-default);
  font-weight: var(--weight-regular);
  font-size: var(--text-lg);
}

/* Context line beside the heading — 'Top 10s | Netflix · Worldwide · Q2 2026'.
   Muted and lighter than the title so the heading still leads. */
.sectionContext {
  font-size: var(--text-base);
  font-weight: var(--weight-regular);
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Inline 'Full leaderboard' link on the heading row — reads as part of the
   context (muted, 14px), turning gold only on hover. */
.headingLink {
  font-size: var(--text-base);
  font-weight: var(--weight-regular);
  color: var(--text-muted);
  text-decoration: none;
  white-space: nowrap;
  transition: color var(--duration-fast) var(--ease-default);
}

.headingLink:hover {
  color: var(--accent-secondary-text);
}

.controls {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-wrap: wrap;
}

/* minmax(0, 1fr) rather than 1fr — a bare 1fr track can't shrink below its
   min-content, so long titles blow the grid out past the page. */
.grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-4);
}

/* Each card needs ~70px rank gutter + 120px poster + text, so two columns stop
   being comfortable well before the usual 1200px breakpoint. */
@media (max-width: 1400px) {
  .grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* ── States ──────────────────────────────────────────────── */

/* Skeletons match the real card footprint, per the d360 carousel pattern */
.cardSkeleton {
  height: 200px;
  border-radius: var(--radius-lg);
  margin-left: 70px;
  background: linear-gradient(
    90deg,
    var(--bg-card) 0%,
    var(--bg-elevated) 50%,
    var(--bg-card) 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s var(--ease-default) infinite;
}

@media (prefers-reduced-motion: reduce) {
  .cardSkeleton {
    animation: none;
  }
}

.empty {
  padding: var(--space-10) var(--space-6);
  text-align: center;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
}

.emptyTitle {
  margin: 0 0 var(--space-2) 0;
  font-size: var(--text-lg);
  font-weight: var(--weight-semi);
  color: var(--text-primary);
}

.emptyBody {
  margin: 0;
  font-size: var(--text-base);
  color: var(--text-muted);
}

.notice {
  padding: var(--space-4);
  margin-bottom: var(--space-6);
  background: var(--color-danger-muted);
  border: 1px solid var(--color-danger);
  border-radius: var(--radius-lg);
  font-size: var(--text-base);
  color: var(--text-primary);
}

@media (max-width: 640px) {
  .page {
    padding: var(--space-4) 0;
  }
}
